/**
 * Gebäude-Check: geführte Erfassung des Anlagenbestands eines Objekts.
 *
 * Statt technischer Formularfelder (Gewerk, kW, Brennstoffcode) beantwortet der Nutzer
 * Fragen, die sich durch Augenschein, Typenschild oder vorhandene Unterlagen beantworten
 * lassen. Daraus entstehen Anlagen, die zutreffenden Prüf- und Wartungspflichten und
 * Klärungspunkte.
 *
 * GRUNDREGEL — Unbekanntes wird nie zu „keine Pflicht":
 *   - Jede Antwort „Nicht bekannt" erzeugt einen Klärungspunkt (Maßnahme mit Frist).
 *   - Bei gesetzlichen Pflichten, deren Regelfall „zutreffend" ist (z. B. Legionellen-
 *     untersuchung im Mehrfamilienhaus mit zentraler Trinkwassererwärmung), wird die Pflicht
 *     zusätzlich vorsorglich angelegt. Sie steht in der Liste, bis sie geklärt ist.
 *
 * Die Pflichtenzuordnung ist eine Vorbelegung, kein Rechtsrat (CLAUDE.md Regel 10). Das
 * Modul enthält keine Datenbank- oder Framework-Abhängigkeit.
 *
 * Schwellenwerte (Quellen: docs/pruefkatalog-recherche.md):
 *   - Großanlage Trinkwassererwärmung (§ 2 TrinkwV): Speicher- oder zentraler Durchfluss-
 *     Trinkwassererwärmer > 400 l ODER > 3 l Inhalt in einer Leitung zwischen Abgang und
 *     Entnahmestelle (ohne Zirkulation). Ausgenommen Ein- und Zweifamilienhäuser.
 *   - F-Gase (EU) 2024/573: Dichtheitsprüfung ab 5 t CO2-Äquivalent.
 *   - Energetische Inspektion Klimaanlagen (§§ 74 ff. GModG): über 12 kW.
 *   - AwSV: unterirdisch stets, oberirdisch über 10.000 l, im Schutzgebiet über 1.000 l.
 */

/* ------------------------------------------------------------------ Typen */

export type SchrittId = 'nutzung' | 'waerme' | 'trinkwasser' | 'technik' | 'sicherheit' | 'grundstueck';

export type FrageId =
  | 'nutzung'
  | 'gebaeudeart'
  | 'waerme'
  | 'heizleistung'
  | 'oellager'
  | 'twe'
  | 'twVolumen'
  | 'twLeitung'
  | 'blei'
  | 'aufzug'
  | 'rlt'
  | 'kaelte'
  | 'kaelteCo2e'
  | 'kaelteLeistung'
  | 'brandschutz'
  | 'blitzschutz'
  | 'entwaesserung'
  | 'aussen';

export interface CheckOption {
  id: string;
  label: string;
}

export interface CheckFrage {
  id: FrageId;
  schritt: SchrittId;
  titel: string;
  erlaeuterung?: string;
  art: 'einfach' | 'mehrfach';
  optionen: CheckOption[];
  /** Wird die Frage gestellt? Nicht gestellte Fragen fließen nicht in die Auswertung. */
  sichtbar?: (a: CheckAntworten, o: CheckObjekt) => boolean;
}

export interface CheckSchritt {
  id: SchrittId;
  titel: string;
  beschreibung: string;
}

export type CheckAntworten = Partial<Record<FrageId, string | string[]>>;

/** Merkmale des Objekts, die der Check aus den Stammdaten übernimmt. */
export interface CheckObjekt {
  baujahr: number | null;
  wohneinheiten: number;
  gewerbeeinheiten: number;
  /** ISO-Kürzel, z. B. „BW". Maßgeblich für Landesrecht (Rauchwarnmelder). */
  bundesland: string | null;
}

export type Verantwortlich = 'eigentuemer' | 'verwalter' | 'mieter';

export interface GeplantePflicht {
  katalogCode: string;
  bezeichnung: string;
  /** Warum die Pflicht zugeordnet wird — erscheint in der Zusammenfassung. */
  grund: string;
  verantwortlich: Verantwortlich;
  /** Zugeordnet, obwohl eine Angabe fehlt (im Zweifel Pflicht). */
  vorsorglich: boolean;
}

export interface GeplanteAnlage {
  /** Stabiler Schlüssel innerhalb eines Check-Ergebnisses. */
  schluessel: string;
  gewerkCode: string;
  bezeichnung: string;
  typ: string | null;
  brennstoff: string | null;
  twGrossanlage: boolean | null;
  pflichten: GeplantePflicht[];
}

export interface Klaerung {
  titel: string;
  beschreibung: string;
  /** Zuordnung zu einer geplanten Anlage, falls vorhanden. */
  anlageSchluessel: string | null;
}

export interface CheckErgebnis {
  anlagen: GeplanteAnlage[];
  klaerungen: Klaerung[];
  hinweise: string[];
}

/* ------------------------------------------------------------------ Katalogbezug */

/**
 * Bezeichnungen der Katalogpflichten, die der Check zuordnet. Muss mit dem Seed in
 * db/migrations/0005_pruefkatalog_seed.sql übereinstimmen — ein Servertest prüft das.
 */
export const CHECK_KATALOG: Readonly<Record<string, string>> = {
  kehrung: 'Kehrung, Überprüfung, Abgaswegeüberprüfung',
  feuerstaettenschau: 'Feuerstättenschau',
  emissionsmessung: 'Emissionsmessung Feuerungsanlage',
  gas_gebrauchsfaehigkeit: 'Gebrauchsfähigkeitsprüfung Gasinstallation',
  oeltank_awsv: 'Heizöltank / AwSV-Anlagen',
  legionellen: 'Legionellenuntersuchung Trinkwasser',
  blei_leitungen: 'Bleileitungen entfernen oder stilllegen',
  aufzug_pruefung: 'Aufzugsprüfung (ZÜS)',
  aufzug_notruf: 'Zweiwege-Notrufsystem Aufzug',
  rlt_hygiene: 'RLT-Hygieneinspektion',
  fgase_dichtheit: 'Dichtheitsprüfung F-Gase',
  klima_inspektion: 'Energetische Inspektion Klimaanlage',
  feuerloescher: 'Feuerlöscher',
  rwa: 'Rauch- und Wärmeabzugsanlagen (RWA)',
  feststellanlagen: 'Feststellanlagen (Brandschutztüren)',
  sicherheitsbeleuchtung: 'Sicherheitsbeleuchtung',
  wandhydrant: 'Wandhydrant / Löschwasserleitung',
  rauchwarnmelder: 'Rauchwarnmelder',
  blitzschutz: 'Blitzschutzanlage',
  rueckstausicherung: 'Rückstauverschluss / Rückstausicherung',
  hebeanlage: 'Abwasserhebeanlage',
  fettabscheider: 'Fettabscheider',
  spielplatz: 'Spielplatzprüfung',
  baumkontrolle: 'Baumkontrolle',
  winterdienst: 'Winterdienst / Räum- und Streupflicht',
};

/** Bundesländer, für die eine Rauchwarnmelder-Katalogpflicht hinterlegt ist. */
export const RAUCHWARNMELDER_LAENDER: readonly string[] = ['BW'];

/* ------------------------------------------------------------------ Fragen */

const NB: CheckOption = { id: 'unbekannt', label: 'Nicht bekannt' };
export const KEINE_AUSWAHL = 'keine';

function wert(a: CheckAntworten, id: FrageId): string | undefined {
  const w = a[id];
  return typeof w === 'string' ? w : undefined;
}

function liste(a: CheckAntworten, id: FrageId): string[] {
  const w = a[id];
  return Array.isArray(w) ? w.filter((x) => x !== KEINE_AUSWAHL) : [];
}

const mitWohnnutzung = (a: CheckAntworten) => wert(a, 'nutzung') !== 'gewerbe';
const fossil = (a: CheckAntworten) =>
  ['erdgas', 'fluessiggas', 'heizoel', 'biomasse'].includes(wert(a, 'waerme') ?? '');
const zentraleTwe = (a: CheckAntworten) =>
  ['zentral_speicher', 'zentral_durchfluss'].includes(wert(a, 'twe') ?? '');

export const CHECK_SCHRITTE: readonly CheckSchritt[] = [
  {
    id: 'nutzung',
    titel: 'Nutzung',
    beschreibung: 'Nutzung und Gebäudeart bestimmen, welche Vorschriften grundsätzlich gelten.',
  },
  {
    id: 'waerme',
    titel: 'Wärmeerzeugung',
    beschreibung: 'Die Art der Wärmeerzeugung bestimmt die Schornsteinfeger-, Mess- und Wartungspflichten.',
  },
  {
    id: 'trinkwasser',
    titel: 'Trinkwasser',
    beschreibung: 'Maßgeblich für die Untersuchungspflicht auf Legionellen nach der Trinkwasserverordnung.',
  },
  {
    id: 'technik',
    titel: 'Gebäudetechnik',
    beschreibung: 'Aufzugsanlagen, raumlufttechnische Anlagen sowie Kälte- und Klimaanlagen.',
  },
  {
    id: 'sicherheit',
    titel: 'Brand- und Blitzschutz',
    beschreibung: 'Vorhandene sicherheitstechnische Einrichtungen im Gebäude.',
  },
  {
    id: 'grundstueck',
    titel: 'Grundstück',
    beschreibung: 'Entwässerungsanlagen und Verkehrssicherungspflichten auf dem Grundstück.',
  },
];

export const CHECK_FRAGEN: readonly CheckFrage[] = [
  /* --- Nutzung --- */
  {
    id: 'nutzung',
    schritt: 'nutzung',
    titel: 'Nutzung des Objekts',
    erlaeuterung: 'Aus den erfassten Einheiten vorbelegt.',
    art: 'einfach',
    optionen: [
      { id: 'wohnen', label: 'Ausschließlich Wohnnutzung' },
      { id: 'gemischt', label: 'Wohn- und Gewerbenutzung' },
      { id: 'gewerbe', label: 'Ausschließlich gewerbliche Nutzung' },
    ],
  },
  {
    id: 'gebaeudeart',
    schritt: 'nutzung',
    titel: 'Gebäudeart',
    erlaeuterung:
      'Ein- und Zweifamilienhäuser sind von der Legionellen-Untersuchungspflicht ausgenommen. ' +
      'Die Anlagen einer Eigentümergemeinschaft betreibt in der Regel die Gemeinschaft.',
    art: 'einfach',
    optionen: [
      { id: 'ezfh', label: 'Ein- oder Zweifamilienhaus' },
      { id: 'mfh', label: 'Mehrfamilienhaus' },
      { id: 'etw', label: 'Eigentumswohnung in einer Wohnungseigentümergemeinschaft' },
    ],
    sichtbar: (a) => wert(a, 'nutzung') !== undefined && mitWohnnutzung(a),
  },

  /* --- Wärme --- */
  {
    id: 'waerme',
    schritt: 'waerme',
    titel: 'Art der Wärmeerzeugung',
    art: 'einfach',
    optionen: [
      { id: 'erdgas', label: 'Gasheizung (Erdgas)' },
      { id: 'fluessiggas', label: 'Gasheizung (Flüssiggas)' },
      { id: 'heizoel', label: 'Ölheizung' },
      { id: 'fernwaerme', label: 'Fernwärme' },
      { id: 'waermepumpe', label: 'Wärmepumpe' },
      { id: 'biomasse', label: 'Holz- oder Pelletheizung' },
      NB,
    ],
  },
  {
    id: 'heizleistung',
    schritt: 'waerme',
    titel: 'Nennwärmeleistung der Heizungsanlage',
    erlaeuterung:
      'Laut Typenschild des Wärmeerzeugers. Für Nichtwohngebäude über 70 kW gilt die Pflicht ' +
      'zur Gebäudeautomation nach § 56 GModG.',
    art: 'einfach',
    optionen: [{ id: 'bis70', label: 'Bis 70 kW' }, { id: 'ueber70', label: 'Über 70 kW' }, NB],
    sichtbar: (a) => wert(a, 'nutzung') === 'gemischt' || wert(a, 'nutzung') === 'gewerbe',
  },
  {
    id: 'oellager',
    schritt: 'waerme',
    titel: 'Lagerung des Heizöls',
    erlaeuterung: 'Maßgeblich für die wiederkehrende Sachverständigenprüfung nach AwSV.',
    art: 'einfach',
    optionen: [
      { id: 'klein', label: 'Oberirdisch, bis 10.000 Liter, außerhalb eines Wasserschutzgebiets' },
      { id: 'gross', label: 'Oberirdisch, über 10.000 Liter' },
      { id: 'unterirdisch', label: 'Unterirdischer Tank' },
      { id: 'schutzgebiet', label: 'Im Wasserschutzgebiet, über 1.000 Liter' },
      NB,
    ],
    sichtbar: (a) => wert(a, 'waerme') === 'heizoel',
  },

  /* --- Trinkwasser --- */
  {
    id: 'twe',
    schritt: 'trinkwasser',
    titel: 'Art der Trinkwassererwärmung',
    art: 'einfach',
    optionen: [
      { id: 'zentral_speicher', label: 'Zentral über Speicher-Trinkwassererwärmer' },
      { id: 'zentral_durchfluss', label: 'Zentral im Durchflussprinzip (z. B. Frischwasserstation)' },
      { id: 'dezentral', label: 'Dezentral in den Nutzungseinheiten (z. B. Durchlauferhitzer)' },
      NB,
    ],
  },
  {
    id: 'twVolumen',
    schritt: 'trinkwasser',
    titel: 'Nenninhalt des Speicher-Trinkwassererwärmers',
    erlaeuterung: 'Laut Typenschild des Speichers.',
    art: 'einfach',
    optionen: [{ id: 'bis400', label: 'Bis 400 Liter' }, { id: 'ueber400', label: 'Über 400 Liter' }, NB],
    sichtbar: (a) => wert(a, 'twe') === 'zentral_speicher',
  },
  {
    id: 'twLeitung',
    schritt: 'trinkwasser',
    titel: 'Leitungsinhalt bis zur entferntesten Entnahmestelle',
    erlaeuterung:
      'Wasserinhalt der Warmwasserleitung zwischen Abgang des Trinkwassererwärmers und ' +
      'Entnahmestelle, ohne Zirkulationsleitung. In Mehrfamilienhäusern wird die Grenze von ' +
      '3 Litern häufig überschritten.',
    art: 'einfach',
    optionen: [{ id: 'bis3', label: 'Bis 3 Liter' }, { id: 'ueber3', label: 'Über 3 Liter' }, NB],
    sichtbar: (a) => zentraleTwe(a),
  },
  {
    id: 'blei',
    schritt: 'trinkwasser',
    titel: 'Trinkwasserleitungen aus Blei',
    erlaeuterung:
      'Bleileitungen mussten nach § 17 Abs. 6 TrinkwV bis zum 12.01.2026 entfernt oder ' +
      'stillgelegt werden. Betrifft vor allem Gebäude mit Baujahr vor 1973.',
    art: 'einfach',
    optionen: [{ id: 'nein', label: 'Nicht vorhanden' }, { id: 'ja', label: 'Vorhanden' }, NB],
    sichtbar: (_a, o) => o.baujahr === null || o.baujahr < 1973,
  },

  /* --- Technik --- */
  {
    id: 'aufzug',
    schritt: 'technik',
    titel: 'Aufzugsanlage',
    art: 'einfach',
    optionen: [
      { id: 'personen', label: 'Personenaufzug' },
      { id: 'lasten', label: 'Lastenaufzug ohne Personenbeförderung' },
      { id: 'keine', label: 'Keine Aufzugsanlage' },
    ],
  },
  {
    id: 'rlt',
    schritt: 'technik',
    titel: 'Raumlufttechnische Anlage (zentrale Lüftungsanlage)',
    art: 'einfach',
    optionen: [{ id: 'ja', label: 'Vorhanden' }, { id: 'nein', label: 'Nicht vorhanden' }, NB],
  },
  {
    id: 'kaelte',
    schritt: 'technik',
    titel: 'Kälte- oder Klimaanlagen',
    art: 'einfach',
    optionen: [{ id: 'ja', label: 'Vorhanden' }, { id: 'nein', label: 'Nicht vorhanden' }, NB],
  },
  {
    id: 'kaelteCo2e',
    schritt: 'technik',
    titel: 'Kältemittelfüllmenge als CO₂-Äquivalent',
    erlaeuterung: 'Laut Typenschild oder Anlagenbuch des Kälteanlagenbauers.',
    art: 'einfach',
    optionen: [
      { id: 'unter5', label: 'Unter 5 t CO₂-Äquivalent' },
      { id: 'ab5', label: '5 t CO₂-Äquivalent oder mehr' },
      NB,
    ],
    sichtbar: (a) => wert(a, 'kaelte') === 'ja',
  },
  {
    id: 'kaelteLeistung',
    schritt: 'technik',
    titel: 'Nennkälteleistung der Klimaanlage',
    art: 'einfach',
    optionen: [{ id: 'bis12', label: 'Bis 12 kW' }, { id: 'ueber12', label: 'Über 12 kW' }, NB],
    sichtbar: (a) => wert(a, 'kaelte') === 'ja',
  },

  /* --- Sicherheit --- */
  {
    id: 'brandschutz',
    schritt: 'sicherheit',
    titel: 'Vorhandene Brandschutzeinrichtungen',
    erlaeuterung: 'Mehrfachauswahl möglich.',
    art: 'mehrfach',
    optionen: [
      { id: 'feuerloescher', label: 'Feuerlöscher' },
      { id: 'rwa', label: 'Rauch- und Wärmeabzugsanlage (RWA)' },
      { id: 'feststellanlagen', label: 'Feststellanlagen an Brandschutztüren' },
      { id: 'sicherheitsbeleuchtung', label: 'Sicherheitsbeleuchtung' },
      { id: 'wandhydrant', label: 'Wandhydranten oder Löschwasserleitungen' },
      { id: KEINE_AUSWAHL, label: 'Keine der genannten Einrichtungen' },
    ],
  },
  {
    id: 'blitzschutz',
    schritt: 'sicherheit',
    titel: 'Äußere Blitzschutzanlage',
    art: 'einfach',
    optionen: [{ id: 'ja', label: 'Vorhanden' }, { id: 'nein', label: 'Nicht vorhanden' }, NB],
  },

  /* --- Grundstück --- */
  {
    id: 'entwaesserung',
    schritt: 'grundstueck',
    titel: 'Entwässerungsanlagen',
    erlaeuterung: 'Mehrfachauswahl möglich.',
    art: 'mehrfach',
    optionen: [
      { id: 'rueckstau', label: 'Rückstauverschluss' },
      { id: 'hebeanlage', label: 'Abwasserhebeanlage' },
      { id: 'fettabscheider', label: 'Fettabscheider' },
      { id: KEINE_AUSWAHL, label: 'Keine der genannten Anlagen' },
    ],
  },
  {
    id: 'aussen',
    schritt: 'grundstueck',
    titel: 'Verkehrssicherung auf dem Grundstück',
    erlaeuterung: 'Mehrfachauswahl möglich.',
    art: 'mehrfach',
    optionen: [
      { id: 'winterdienst', label: 'Räum- und Streupflicht für Gehwege' },
      { id: 'baeume', label: 'Baumbestand auf dem Grundstück' },
      { id: 'spielplatz', label: 'Spielplatz' },
      { id: KEINE_AUSWAHL, label: 'Keine der genannten' },
    ],
  },
];

/* ------------------------------------------------------------------ Hilfen für die UI */

export function sichtbareFragen(schritt: SchrittId, a: CheckAntworten, o: CheckObjekt): CheckFrage[] {
  return CHECK_FRAGEN.filter((f) => f.schritt === schritt && (f.sichtbar ? f.sichtbar(a, o) : true));
}

/** Unbeantwortete, gestellte Fragen eines Schritts. Jede gestellte Frage ist Pflicht. */
export function offeneFragen(schritt: SchrittId, a: CheckAntworten, o: CheckObjekt): CheckFrage[] {
  return sichtbareFragen(schritt, a, o).filter((f) => {
    const w = a[f.id];
    if (f.art === 'mehrfach') return !Array.isArray(w) || w.length === 0;
    return typeof w !== 'string' || w === '';
  });
}

/** Mehrfachauswahl: „Keine der genannten" schließt alle übrigen Optionen aus und umgekehrt. */
export function mehrfachUmschalten(aktuell: string[] | undefined, id: string): string[] {
  const vorher = aktuell ?? [];
  if (id === KEINE_AUSWAHL) return vorher.includes(KEINE_AUSWAHL) ? [] : [KEINE_AUSWAHL];
  const ohneKeine = vorher.filter((x) => x !== KEINE_AUSWAHL);
  return ohneKeine.includes(id) ? ohneKeine.filter((x) => x !== id) : [...ohneKeine, id];
}

/** Vorbelegung aus den Stammdaten des Objekts. Der Nutzer bestätigt oder ändert. */
export function vorbelegung(o: CheckObjekt): CheckAntworten {
  const a: CheckAntworten = {};
  if (o.wohneinheiten > 0 && o.gewerbeeinheiten > 0) a.nutzung = 'gemischt';
  else if (o.gewerbeeinheiten > 0) a.nutzung = 'gewerbe';
  else if (o.wohneinheiten > 0) a.nutzung = 'wohnen';
  if (o.wohneinheiten > 0) a.gebaeudeart = o.wohneinheiten <= 2 ? 'ezfh' : 'mfh';
  return a;
}

/** Nur Antworten auf tatsächlich gestellte Fragen und nur gültige Optionen berücksichtigen. */
export function bereinigteAntworten(a: CheckAntworten, o: CheckObjekt): CheckAntworten {
  const aus: CheckAntworten = {};
  for (const f of CHECK_FRAGEN) {
    if (f.sichtbar && !f.sichtbar(aus, o)) continue;
    const w = a[f.id];
    const gueltig = new Set(f.optionen.map((x) => x.id));
    if (f.art === 'einfach' && typeof w === 'string' && gueltig.has(w)) aus[f.id] = w;
    if (f.art === 'mehrfach' && Array.isArray(w)) aus[f.id] = w.filter((x) => gueltig.has(x));
  }
  return aus;
}

/* ------------------------------------------------------------------ Auswertung */

const BRENNSTOFF: Record<string, string> = {
  erdgas: 'gas',
  fluessiggas: 'fluessiggas',
  heizoel: 'heizoel',
  fernwaerme: 'fernwaerme',
  waermepumpe: 'waermepumpe',
  biomasse: 'holz',
};

const WAERME_BEZEICHNUNG: Record<string, string> = {
  erdgas: 'Gasheizung (Erdgas)',
  fluessiggas: 'Gasheizung (Flüssiggas)',
  heizoel: 'Ölheizung',
  fernwaerme: 'Fernwärmeübergabestation',
  waermepumpe: 'Wärmepumpe',
  biomasse: 'Holz- oder Pelletheizung',
  unbekannt: 'Wärmeerzeugung (Art ungeklärt)',
};

const GROSSANLAGE_EZFH =
  'Ein- und Zweifamilienhäuser sind von der Untersuchungspflicht als Großanlage ausgenommen.';

export function gebaeudeCheckAuswerten(antworten: CheckAntworten, o: CheckObjekt): CheckErgebnis {
  const a = bereinigteAntworten(antworten, o);
  const anlagen: GeplanteAnlage[] = [];
  const klaerungen: Klaerung[] = [];
  const hinweise: string[] = [];

  const etw = wert(a, 'gebaeudeart') === 'etw';
  const ezfh = wert(a, 'gebaeudeart') === 'ezfh';
  const standard: Verantwortlich = etw ? 'verwalter' : 'eigentuemer';

  if (etw) {
    hinweise.push(
      'Gemeinschaftliche Anlagen einer Wohnungseigentümergemeinschaft betreibt in der Regel die ' +
        'Gemeinschaft. Die Pflichten sind deshalb der Verwaltung zugeordnet; Nachweise sind dort ' +
        'anzufordern.',
    );
  }

  function anlage(
    schluessel: string,
    gewerkCode: string,
    bezeichnung: string,
    extra: Partial<Pick<GeplanteAnlage, 'typ' | 'brennstoff' | 'twGrossanlage'>> = {},
  ): GeplanteAnlage {
    const neu: GeplanteAnlage = {
      schluessel,
      gewerkCode,
      bezeichnung,
      typ: extra.typ ?? null,
      brennstoff: extra.brennstoff ?? null,
      twGrossanlage: extra.twGrossanlage ?? null,
      pflichten: [],
    };
    anlagen.push(neu);
    return neu;
  }

  function pflicht(
    ziel: GeplanteAnlage,
    katalogCode: string,
    grund: string,
    opt: { vorsorglich?: boolean; verantwortlich?: Verantwortlich } = {},
  ): void {
    const bezeichnung = CHECK_KATALOG[katalogCode];
    if (!bezeichnung) throw new Error(`Unbekannte Katalogpflicht im Gebäude-Check: ${katalogCode}`);
    if (ziel.pflichten.some((p) => p.katalogCode === katalogCode)) return;
    ziel.pflichten.push({
      katalogCode,
      bezeichnung,
      grund,
      verantwortlich: opt.verantwortlich ?? standard,
      vorsorglich: opt.vorsorglich ?? false,
    });
  }

  function klaeren(titel: string, beschreibung: string, ziel: GeplanteAnlage | null = null): void {
    klaerungen.push({ titel, beschreibung, anlageSchluessel: ziel?.schluessel ?? null });
  }

  /* --- Wärmeerzeugung --- */
  const waerme = wert(a, 'waerme');
  if (waerme) {
    const heizung = anlage('heizung', 'heizung', WAERME_BEZEICHNUNG[waerme] ?? 'Wärmeerzeugung', {
      brennstoff: BRENNSTOFF[waerme] ?? null,
    });
    if (fossil(a)) {
      pflicht(heizung, 'kehrung', 'Feuerungsanlage nach Kehr- und Überprüfungsordnung (KÜO)');
      pflicht(heizung, 'feuerstaettenschau', 'Feuerstättenschau durch den bevollmächtigten Bezirksschornsteinfeger');
      pflicht(heizung, 'emissionsmessung', 'Feuerungsanlage nach 1. BImSchV');
    }
    if (waerme === 'erdgas') {
      const gas = anlage('gas', 'gas', 'Gasinstallation (Erdgas)');
      pflicht(gas, 'gas_gebrauchsfaehigkeit', 'Erdgasinstallation nach DVGW-TRGI (anerkannte Regel der Technik)');
    }
    if (waerme === 'fluessiggas') {
      hinweise.push(
        'Für Flüssiggasanlagen gelten gesonderte Prüfpflichten (BetrSichV, TRF). Diese sind im ' +
          'Prüfkatalog noch nicht hinterlegt und müssen gesondert erfasst werden.',
      );
    }
    if (waerme === 'heizoel') {
      const lager = wert(a, 'oellager');
      const tank = anlage('tank', 'tank', 'Heizöltank');
      if (lager === 'gross' || lager === 'unterirdisch' || lager === 'schutzgebiet') {
        pflicht(tank, 'oeltank_awsv', 'Prüfpflichtige Anlage nach AwSV (Lage bzw. Volumen)');
      } else if (lager === 'unbekannt') {
        pflicht(tank, 'oeltank_awsv', 'Lage und Volumen des Tanks nicht bekannt', { vorsorglich: true });
        klaeren(
          'Heizöltank einstufen',
          'Lage (ober- oder unterirdisch), Volumen und Lage in einem Wasserschutzgebiet ' +
            'feststellen. Davon hängt die Prüfpflicht nach AwSV ab.',
          tank,
        );
      }
    }
    if (waerme === 'unbekannt') {
      klaeren(
        'Art der Wärmeerzeugung feststellen',
        'Energieträger und Bauart des Wärmeerzeugers feststellen, z. B. anhand des ' +
          'Feuerstättenbescheids oder durch den Fachbetrieb. Davon hängen Kehr-, Mess- und ' +
          'Wartungspflichten ab.',
        heizung,
      );
    }
    const heizleistung = wert(a, 'heizleistung');
    if (heizleistung === 'ueber70' || heizleistung === 'unbekannt') {
      klaeren(
        'Pflicht zur Gebäudeautomation prüfen',
        'Nichtwohngebäude mit Heizungs- oder kombinierten Lüftungsanlagen über 70 kW sind nach ' +
          '§ 56 GModG bis 31.12.2029 mit einem System zur Gebäudeautomation auszustatten.',
        heizung,
      );
    }
    if (o.wohneinheiten >= 6) {
      hinweise.push(
        'Ab sechs Wohneinheiten werden die Pflichten nach §§ 60a–60c GModG (Heizungsprüfung, ' +
          'hydraulischer Abgleich, Betriebsprüfung Wärmepumpe) beim Anlegen automatisch ergänzt.',
      );
    }
  }

  /* --- Trinkwasser --- */
  const twe = wert(a, 'twe');
  let trinkwasser: GeplanteAnlage | null = null;
  if (twe === 'zentral_speicher' || twe === 'zentral_durchfluss') {
    const vol = wert(a, 'twVolumen');
    const leitung = wert(a, 'twLeitung');
    const ueberSchwelle = vol === 'ueber400' || leitung === 'ueber3';
    const unklar = vol === 'unbekannt' || leitung === 'unbekannt';
    const gross = ezfh ? false : ueberSchwelle || unklar;
    trinkwasser = anlage(
      'trinkwasser',
      'trinkwasser',
      twe === 'zentral_speicher'
        ? 'Speicher-Trinkwassererwärmer (zentral)'
        : 'Trinkwassererwärmung im Durchflussprinzip (zentral)',
      { twGrossanlage: gross, typ: twe === 'zentral_speicher' ? 'speicher' : 'durchfluss' },
    );
    if (ezfh) {
      hinweise.push(GROSSANLAGE_EZFH);
    } else if (ueberSchwelle) {
      pflicht(trinkwasser, 'legionellen', 'Großanlage zur Trinkwassererwärmung nach § 2 TrinkwV');
    } else if (unklar) {
      pflicht(trinkwasser, 'legionellen', 'Einstufung als Großanlage nicht bestätigt', { vorsorglich: true });
      klaeren(
        'Einstufung als Großanlage bestätigen',
        'Nenninhalt des Trinkwassererwärmers und Leitungsinhalt bis zur entferntesten ' +
          'Entnahmestelle durch den Fachbetrieb feststellen lassen. Bis zur Klärung bleibt die ' +
          'Untersuchungspflicht hinterlegt.',
        trinkwasser,
      );
    } else {
      hinweise.push(
        'Die Trinkwassererwärmung liegt unter den Schwellen einer Großanlage. Eine regelmäßige ' +
          'Untersuchungspflicht auf Legionellen besteht danach nicht.',
      );
    }
  } else if (twe === 'unbekannt' && !ezfh) {
    trinkwasser = anlage('trinkwasser', 'trinkwasser', 'Trinkwassererwärmung (Art ungeklärt)');
    pflicht(trinkwasser, 'legionellen', 'Art der Trinkwassererwärmung nicht bekannt', { vorsorglich: true });
    klaeren(
      'Art der Trinkwassererwärmung feststellen',
      'Feststellen, ob das Warmwasser zentral oder dezentral erzeugt wird. Bei zentraler ' +
        'Erwärmung im Mehrfamilienhaus besteht in der Regel eine Untersuchungspflicht auf ' +
        'Legionellen.',
      trinkwasser,
    );
  } else if (twe === 'unbekannt' && ezfh) {
    hinweise.push(GROSSANLAGE_EZFH);
  }

  const blei = wert(a, 'blei');
  if (blei === 'ja') {
    const ziel = trinkwasser ?? anlage('trinkwasserinstallation', 'trinkwasser', 'Trinkwasserinstallation');
    pflicht(ziel, 'blei_leitungen', 'Bleileitungen vorhanden, Frist nach § 17 Abs. 6 TrinkwV abgelaufen');
  } else if (blei === 'unbekannt') {
    klaeren(
      'Leitungswerkstoffe prüfen',
      'Feststellen, ob Trinkwasserleitungen aus Blei vorhanden sind. Nach § 17 Abs. 6 TrinkwV ' +
        'waren sie bis zum 12.01.2026 zu entfernen oder stillzulegen.',
      trinkwasser,
    );
  }

  /* --- Technik --- */
  const aufzug = wert(a, 'aufzug');
  if (aufzug === 'personen') {
    const z = anlage('aufzug', 'aufzug', 'Personenaufzug');
    pflicht(z, 'aufzug_pruefung', 'Überwachungsbedürftige Anlage nach BetrSichV');
    pflicht(z, 'aufzug_notruf', 'Personenbeförderung, Zweiwege-Kommunikation erforderlich');
  } else if (aufzug === 'lasten') {
    const z = anlage('aufzug', 'aufzug', 'Lastenaufzug');
    pflicht(z, 'aufzug_pruefung', 'Überwachungsbedürftige Anlage nach BetrSichV');
  }

  const rlt = wert(a, 'rlt');
  if (rlt === 'ja') {
    const r = anlage('rlt', 'lueftung', 'Raumlufttechnische Anlage');
    pflicht(r, 'rlt_hygiene', 'Hygieneinspektion nach VDI 6022 (anerkannte Regel der Technik)');
  } else if (rlt === 'unbekannt') {
    klaeren('Raumlufttechnische Anlagen feststellen', 'Feststellen, ob eine zentrale Lüftungsanlage betrieben wird.');
  }

  const kaelte = wert(a, 'kaelte');
  if (kaelte === 'ja') {
    const k = anlage('klima', 'klima', 'Kälte- oder Klimaanlage');
    const co2e = wert(a, 'kaelteCo2e');
    const leistung = wert(a, 'kaelteLeistung');
    if (co2e === 'ab5') pflicht(k, 'fgase_dichtheit', 'Ab 5 t CO₂-Äquivalent nach F-Gase-Verordnung');
    if (co2e === 'unbekannt') {
      pflicht(k, 'fgase_dichtheit', 'Füllmenge nicht bekannt', { vorsorglich: true });
      klaeren(
        'Kältemittelfüllmenge feststellen',
        'Kältemittel und Füllmenge vom Typenschild ablesen. Maßgeblich ist das CO₂-Äquivalent ' +
          '(Füllmenge × Treibhauspotenzial).',
        k,
      );
    }
    if (co2e === 'unter5') {
      hinweise.push(
        'Unter 5 t CO₂-Äquivalent besteht keine regelmäßige Dichtheitsprüfung nach der ' +
          'CO₂-Schwelle. Für bestimmte Kältemittel (Anhang II) gilt zusätzlich eine kg-Schwelle.',
      );
    }
    if (leistung === 'ueber12') pflicht(k, 'klima_inspektion', 'Nennkälteleistung über 12 kW (§§ 74 ff. GModG)');
    if (leistung === 'unbekannt') {
      pflicht(k, 'klima_inspektion', 'Nennkälteleistung nicht bekannt', { vorsorglich: true });
      klaeren('Nennkälteleistung feststellen', 'Nennkälteleistung der Klimaanlage vom Typenschild ablesen.', k);
    }
  } else if (kaelte === 'unbekannt') {
    klaeren('Kälte- und Klimaanlagen feststellen', 'Feststellen, ob Kälte- oder Klimaanlagen betrieben werden.');
  }

  /* --- Brandschutz --- */
  const BRANDSCHUTZ: Record<string, [string, string]> = {
    feuerloescher: ['Feuerlöscher', 'feuerloescher'],
    rwa: ['Rauch- und Wärmeabzugsanlage', 'rwa'],
    feststellanlagen: ['Feststellanlagen an Brandschutztüren', 'feststellanlagen'],
    sicherheitsbeleuchtung: ['Sicherheitsbeleuchtung', 'sicherheitsbeleuchtung'],
    wandhydrant: ['Wandhydranten / Löschwasserleitung', 'wandhydrant'],
  };
  for (const id of liste(a, 'brandschutz')) {
    const eintrag = BRANDSCHUTZ[id];
    if (!eintrag) continue;
    const b = anlage(`brandschutz_${id}`, 'brandschutz', eintrag[0]);
    pflicht(b, eintrag[1], 'Vorhandene Brandschutzeinrichtung');
  }

  if (wert(a, 'nutzung') !== undefined && mitWohnnutzung(a)) {
    if (o.bundesland && RAUCHWARNMELDER_LAENDER.includes(o.bundesland)) {
      const r = anlage('rauchwarnmelder', 'brandschutz', 'Rauchwarnmelder');
      // BW, § 15 Abs. 9 LBO: Einbau durch den Eigentümer, Sicherstellung der
      // Betriebsbereitschaft durch den unmittelbaren Besitzer (Mieter).
      pflicht(r, 'rauchwarnmelder', 'Rauchwarnmelderpflicht nach Landesbauordnung', {
        verantwortlich: o.bundesland === 'BW' ? 'mieter' : standard,
      });
      if (o.bundesland === 'BW') {
        hinweise.push(
          'Baden-Württemberg: Der Einbau der Rauchwarnmelder obliegt dem Eigentümer, die ' +
            'Sicherstellung der Betriebsbereitschaft dem unmittelbaren Besitzer (§ 15 Abs. 9 LBO).',
        );
      }
    } else if (o.bundesland) {
      hinweise.push(
        `Rauchwarnmelderpflicht nach Landesrecht (${o.bundesland}): für dieses Bundesland noch ` +
          'nicht im Prüfkatalog hinterlegt. Bitte gesondert erfassen.',
      );
    } else {
      klaeren(
        'Bundesland am Objekt hinterlegen',
        'Rauchwarnmelder- und weitere Landesvorschriften hängen vom Bundesland ab.',
      );
    }
  }

  const blitz = wert(a, 'blitzschutz');
  if (blitz === 'ja') {
    const b = anlage('blitzschutz', 'blitzschutz', 'Äußere Blitzschutzanlage');
    pflicht(b, 'blitzschutz', 'Wiederkehrende Prüfung nach DIN EN 62305-3 (anerkannte Regel der Technik)');
  } else if (blitz === 'unbekannt') {
    klaeren('Blitzschutzanlage feststellen', 'Feststellen, ob eine äußere Blitzschutzanlage vorhanden ist.');
  }

  /* --- Grundstück --- */
  const ENTWAESSERUNG: Record<string, [string, string]> = {
    rueckstau: ['Rückstauverschluss', 'rueckstausicherung'],
    hebeanlage: ['Abwasserhebeanlage', 'hebeanlage'],
    fettabscheider: ['Fettabscheider', 'fettabscheider'],
  };
  for (const id of liste(a, 'entwaesserung')) {
    const e = ENTWAESSERUNG[id];
    if (!e) continue;
    const x = anlage(`abwasser_${id}`, 'abwasser', e[0]);
    pflicht(x, e[1], 'Wartung nach DIN 1986-3 bzw. DIN EN 12056 (anerkannte Regel der Technik)');
  }

  const AUSSEN: Record<string, [string, string, string]> = {
    winterdienst: ['Gehwege mit Räum- und Streupflicht', 'winterdienst', 'Verkehrssicherungspflicht, örtliche Satzung'],
    baeume: ['Baumbestand', 'baumkontrolle', 'Verkehrssicherungspflicht (§ 823 BGB)'],
    spielplatz: ['Spielplatz', 'spielplatz', 'Verkehrssicherungspflicht, DIN EN 1176'],
  };
  for (const id of liste(a, 'aussen')) {
    const e = AUSSEN[id];
    if (!e) continue;
    const x = anlage(`aussen_${id}`, 'aussen', e[0]);
    pflicht(x, e[1], e[2]);
  }

  if (wert(a, 'nutzung') === 'gemischt' || wert(a, 'nutzung') === 'gewerbe') {
    klaeren(
      'Prüfpflicht ortsfester elektrischer Anlagen klären',
      'Für Gewerbe- und Allgemeinflächen mit Beschäftigten kann eine wiederkehrende Prüfung nach ' +
        'DGUV Vorschrift 3 bestehen. Zuständigkeit zwischen Vermieter und gewerblichem Mieter klären.',
    );
  }

  return { anlagen, klaerungen, hinweise };
}

/** Kennzahlen für die Zusammenfassung. */
export function checkZusammenfassung(e: CheckErgebnis): {
  anlagen: number;
  pflichten: number;
  vorsorglich: number;
  klaerungen: number;
} {
  const alle = e.anlagen.flatMap((x) => x.pflichten);
  return {
    anlagen: e.anlagen.length,
    pflichten: alle.length,
    vorsorglich: alle.filter((p) => p.vorsorglich).length,
    klaerungen: e.klaerungen.length,
  };
}
